import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Phone, Sparkles } from "lucide-react";
import { aiChat } from "../lib/api";
import { ChatBubble } from "./chatbot/ChatBubble";
import { ChatSuggestions, QualifiedCta } from "./chatbot/ChatExtras";

const STORAGE_KEY = "gmc_chat_session";
const HISTORY_KEY = "gmc_chat_history";

const SUGGESTIONS = [
  "J'ai des fissures sur ma façade",
  "Combien coûte un ravalement ?",
  "Comment fonctionne l'imitation pierre ?",
  "Je veux visualiser ma façade rénovée",
];

const WELCOME = {
  id: "welcome",
  role: "assistant",
  content:
    "👋 Bonjour ! Je suis l'assistant GMC. Je peux vous orienter sur votre projet de façade, vous aider à obtenir un diagnostic gratuit ou un rendez-vous devis à domicile avec un expert.\n\nQuelle est votre question ?",
};

function makeId() {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function loadHistory() {
  try {
    const saved = sessionStorage.getItem(HISTORY_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (err) {
    console.error("ChatBot history restore failed", err);
  }
  return [WELCOME];
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || null;
    } catch (err) {
      console.error("ChatBot sessionStorage read failed", err);
      return null;
    }
  });
  const scrollRef = useRef(null);

  // Persist history
  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    } catch (err) {
      console.error("ChatBot history persist failed", err);
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const showQualifiedCta = userMessageCount >= 2 && !sending;

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    const next = [...messages, { id: makeId(), role: "user", content }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const apiMessages = next
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(-12)
        .map(({ role, content: c }) => ({ role, content: c }));
      const res = await aiChat(sessionId, apiMessages);
      if (res.session_id && res.session_id !== sessionId) {
        setSessionId(res.session_id);
        try {
          sessionStorage.setItem(STORAGE_KEY, res.session_id);
        } catch (err) {
          console.error("ChatBot session persist failed", err);
        }
      }
      setMessages((m) => [...m, { id: makeId(), role: "assistant", content: res.reply }]);
    } catch (err) {
      console.error("ChatBot send failed", err);
      setMessages((m) => [
        ...m,
        {
          id: makeId(),
          role: "assistant",
          content:
            "Désolé, je rencontre un problème technique. Vous pouvez nous appeler directement au **07 63 43 38 67** — un expert vous répondra rapidement.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Launcher button */}
      <motion.button
        type="button"
        data-testid="chat-launcher"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
        className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FF5A00] text-white shadow-2xl shadow-[#FF5A00]/40 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        aria-label={open ? "Fermer le chat" : "Ouvrir le chat GMC"}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="x" initial={{ rotate: -90 }} animate={{ rotate: 0 }} exit={{ rotate: 90 }}>
              <X size={24} />
            </motion.div>
          ) : (
            <motion.div key="msg" initial={{ rotate: -90 }} animate={{ rotate: 0 }} exit={{ rotate: 90 }}>
              <MessageCircle size={24} />
            </motion.div>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-[#050505] animate-pulse" />
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-3 md:right-8 z-50 w-[calc(100vw-1.5rem)] md:w-[400px] max-w-md h-[calc(100vh-8rem)] max-h-[600px] glass-strong rounded-3xl flex flex-col overflow-hidden border border-white/10 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-5 border-b border-white/10 bg-black/60">
              <div className="w-10 h-10 rounded-full bg-[#FF5A00] flex items-center justify-center shrink-0">
                <Sparkles size={16} />
              </div>
              <div className="flex-1">
                <div className="font-display text-base text-white leading-none">
                  GMC Assistant
                </div>
                <div className="text-[11px] text-emerald-400 flex items-center gap-1.5 mt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  En ligne · Réponse en quelques secondes
                </div>
              </div>
              <a
                href="tel:+33763433867"
                data-testid="chat-call-btn"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-[#FF5A00] hover:border-[#FF5A00] transition-colors"
                aria-label="Appeler GMC"
              >
                <Phone size={14} />
              </a>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto p-5 space-y-3"
            >
              {messages.map((m) => (
                <ChatBubble key={m.id} role={m.role} content={m.content} />
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-neutral-400 text-sm py-2 pl-2">
                  <Loader2 size={14} className="animate-spin text-[#FF5A00]" />
                  GMC Assistant écrit…
                </div>
              )}

              {messages.length === 1 && !sending && (
                <ChatSuggestions suggestions={SUGGESTIONS} onPick={send} />
              )}

              {showQualifiedCta && <QualifiedCta onClose={() => setOpen(false)} />}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-black/40">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder="Posez votre question…"
                  disabled={sending}
                  data-testid="chat-input"
                  className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-[#FF5A00] resize-none max-h-24 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={!input.trim() || sending}
                  data-testid="chat-send-btn"
                  className="w-10 h-10 rounded-full bg-[#FF5A00] flex items-center justify-center text-white shrink-0 disabled:opacity-50 hover:scale-105 active:scale-95 transition-transform"
                  aria-label="Envoyer"
                >
                  <Send size={14} />
                </button>
              </div>
              <p className="text-[10px] text-neutral-600 mt-2 text-center">
                Réponses IA — Pour un devis précis :{" "}
                <a href="tel:+33763433867" className="text-[#FF5A00]">
                  07 63 43 38 67
                </a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
